import { Component, ElementRef, OnDestroy, OnInit, ViewChild, WritableSignal, inject, signal } from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute } from "@angular/router";
import { Device } from "@capacitor/device";
import { Network } from "@capacitor/network";
import { AlertController, MenuController, ModalController, NavController, Platform } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Subscription, combineLatest, firstValueFrom, interval } from "rxjs";
import { take } from "rxjs/operators";
import { InitAuth, InitEvents, InitUser, Loading } from "src/app/shared/actions/utility.actions";
import { PasswordSecurity } from "src/app/shared/enums/password-security";
import { TypeLogin } from "src/app/shared/enums/type-login";
import { TypeUser } from "src/app/shared/enums/type-user";
import { IContainer, IEvent, IUser } from "src/app/shared/interfaces";
import { PathComponents } from "src/app/shared/paths/path-components";
import { getContainersState } from "src/app/shared/selectors/containers.selectors";
import { AuthService, EventUsersService, EventsService, FormsService, UtilityService } from "src/app/shared/services";
import { environment } from "src/environments/environment";
import { FavoritesService } from "src/app/shared/services/favorites.service";
import { OtpInputComponent } from "src/app/shared/generic-components/otp-input/otp-input.component";
import { Capacitor } from "@capacitor/core";

@Component({
	selector: "app-login",
	templateUrl: "./login.page.html",
	styleUrls: ["./login.page.scss"],
	standalone: false
})
export class LoginPage implements OnInit, OnDestroy {
	@ViewChild(OtpInputComponent) otpInputComponent: OtpInputComponent;
	subscriptions: Subscription[] = [];
	interval: Subscription;
	loginLogoSize: string = environment.platform.loginLogoClass;
	containerId: string = environment.platform.containerId;

	loader: WritableSignal<boolean> = signal(true);
	activeLoader: WritableSignal<boolean> = signal(false);

	container: IContainer;
	user: IUser;
	eventId: string;
	event: IEvent;
	codeNumber: string = "";
	currentOtpValue: string = "";
	isShortcodeOtpComplete: boolean = false;

	count: number = 180;
	timeRemaining: string;

	passwordScreen: boolean = false;

	loginForm: UntypedFormGroup;
	minLengthPasswordChar: number = 6;

	emailView: boolean = false;
	createPasswordView: boolean = false;
	passwordView: boolean = false;
	firstAccessView: boolean = false;
	shortcodeView: boolean = false;

	showPwd: boolean = false;
	isMobile: boolean;
	emailCodeLanguage: string;
	appActive: boolean = true;
	networkStatus: boolean = true;

	// --- Injected Services ---
	private route = inject(ActivatedRoute);
	private menu = inject(MenuController);
	private modalCtrl = inject(ModalController);
	private alertCtrl = inject(AlertController);
	private navCtrl = inject(NavController);
	private fb = inject(UntypedFormBuilder);
	private store = inject(Store);
	private SAuth = inject(AuthService);
	private SEvents = inject(EventsService);
	private STranslate = inject(TranslateService);
	private SUtility = inject(UtilityService);
	private snackbar = inject(MatSnackBar);
	private elementRef = inject(ElementRef);
	private platform = inject(Platform);
	private SEventUsers = inject(EventUsersService);
	private SForms = inject(FormsService);
	private favoritesService = inject(FavoritesService);
	// --- End Injected Services ---

	constructor() {
		Network.addListener("networkStatusChange", (status) => {
			this.networkStatus = status.connected;
			if (!status.connected) {
				// snackbar with message "You are offline"
				this.snackbar.open(this.STranslate.instant("texts.network_offline"), "OK", {
					duration: 3000,
					panelClass: "error-snackbar"
				});
			}
		});

		this.menu.enable(false);

		this.getDeviceLanguage();

		this.route.params.pipe(take(1)).subscribe((params) => {
			this.eventId = params.eventId ? params.eventId : "";
		});

		this.loginForm = this.fb.group({
			email: ["", [Validators.required, this.SForms.customEmailValidators()]],
			password: ["", [Validators.required, Validators.minLength(this.minLengthPasswordChar)]],
			confirmPassword: [""],
			codeNumber: [""],
			termCheck: [false]
		});

		// Removed automatic disabling of termCheck - users should be able to uncheck it

		this.isMobile = this.platform.is("ios") || this.platform.is("android");
	}

	ngOnInit() {
		document.body.style.setProperty("--b3-navbar-height", "0px");

		if (this.interval && !this.interval.closed) {
			this.interval.unsubscribe();
		}
		this.subscriptions.push(
			this.store.select(getContainersState).subscribe({
				next: (containersState) => {
					this.container =
						!environment.platform.containerId ||
						environment.platform.containerId === "defaultContainer" ||
						!containersState.currentContainer
							? containersState.defaultContainer
							: containersState.currentContainer;

					if (this.container) {
						if (
							this.container.loginSettings.type === TypeLogin.WITH_EMAIL_CONFIRM ||
							this.container.loginSettings.type === TypeLogin.WITHOUT_EMAIL_CONFIRM
						) {
							this.changeView(true, false, false, false, false);
						} else if (this.container.loginSettings.type === TypeLogin.WITH_SHORTCODE) {
							this.changeView(false, false, false, false, true);
							if (!this.loginForm.get("shortcode")) {
								this.loginForm.addControl("shortcode", new UntypedFormControl(""));
							}
						}
					}
					this.loader.set(false);
					this.store.dispatch(Loading({ payload: false }));
				},
				error: (error) => {
					console.error("❌ [LOGIN] Error in containersState subscription:", error);
				}
			})
		);
	}

	ngOnDestroy() {
		this.subscriptions.forEach((sub) => sub?.unsubscribe());
		if (this.interval && !this.interval.closed) {
			this.interval.unsubscribe();
		}
	}

	/**
	 * First step verif email and send code if necessary
	 */
	async firstStepVerifEmail() {
		// Validate form before processing
		if (!this.loginForm.get("email").valid) {
			this.snackbar.open(this.STranslate.instant("login.invalid_email"), "", {
				duration: 3000,
				panelClass: "error-snackbar"
			});
			return;
		}

		const data = this.loginForm.getRawValue();
		try {
			this.activeLoader.set(true);
			if (data.email) {
				const results: any[] = await firstValueFrom(
					combineLatest([
						this.SAuth.checkAuthAccountExist(data.email, "", "email"),
						this.SAuth.getUserByEmail(data.email.toLowerCase())
					])
				);
				const authAccountResult = results[0];
				this.user = results[1];
				if (
					authAccountResult &&
					authAccountResult.code === 200 &&
					this.user &&
					authAccountResult.result &&
					authAccountResult.result.uid === this.user.uid &&
					authAccountResult.result &&
					authAccountResult.result.email === this.user.email
				) {
					// If auth account and user data exist and auth account uid
					//and email same than user uid and email all good for continuing login user
					// Disable email for not changing it
					this.loginForm.get("email").disable();
					// Change view to email view and password view
					this.changeView(true, false, true, false, false);
					this.activeLoader.set(false);
				} else if (
					this.user &&
					(!authAccountResult || authAccountResult.code === 204) &&
					this.user.type >= TypeUser.SPEAKER
				) {
					// If user exist but no auth account
					this.loginForm.get("email").disable();
					// Change view to first access view or create pass view
					if (this.container.loginSettings.type === TypeLogin.WITH_EMAIL_CONFIRM) {
						this.changeView(false, false, false, true, false);
						this.createCodeNumber();
					} else {
						this.changeView(false, true, false, false, false);
					}
				} else {
					if (!this.networkStatus) {
						this.snackbar.open(this.STranslate.instant("snackbar.no_network"), "", {
							duration: 3000,
							panelClass: "error-snackbar"
						});
					} else {
						// User not found
						this.snackbar.open(this.STranslate.instant("login.email_not_found"), "", {
							duration: 3000,
							panelClass: "error-snackbar"
						});
					}
					this.activeLoader.set(false);
				}
			}

			this.activeLoader.set(false);
		} catch (error) {
			this.activeLoader.set(false);
			this.snackbar.open(this.STranslate.instant("snackbar.error_occured"), "", {
				duration: 3000,
				panelClass: "error-snackbar"
			});
		}
	}

	/**
	 * To create account, the user enter your password two times,
	 * check both is equal and add to Firebase Authentication and update in Firestore
	 */
	async createAccount() {
		// Validate if already loading
		// if (this.activeLoader) {
		// 	return;
		// }

		this.minLengthPasswordChar = this.container.loginSettings.passwordSecurityLevel === 1 ? 6 : 12;
		const data = this.loginForm.getRawValue();

		// Pre-validation checks
		if (!data.password || data.password.length < this.minLengthPasswordChar) {
			this.snackbar.open(this.STranslate.instant("login.invalid_pass"), "", {
				duration: 3000,
				panelClass: "error-snackbar"
			});
			return;
		}

		if (data.password !== data.confirmPassword) {
			this.snackbar.open(this.STranslate.instant("login.password_not_match"), "", {
				duration: 3000,
				panelClass: "error-snackbar"
			});
			return;
		}

		if (!data.termCheck) {
			this.snackbar.open(this.STranslate.instant("login.term_check_required"), "", {
				duration: 3000,
				panelClass: "error-snackbar"
			});
			return;
		}

		if (!this.user) {
			this.snackbar.open(this.STranslate.instant("snackbar.no_user"), "", {
				duration: 3000,
				panelClass: "error-snackbar"
			});
			return;
		}

		try {
			this.activeLoader.set(true);
			await firstValueFrom(this.SAuth.createOnlyAuthAccount(this.user, data.password));
			this.login();
			// this.activeLoader = false;
		} catch (error) {
			this.activeLoader.set(false);
			this.snackbar.open(this.STranslate.instant("snackbar.cannot_create_account"), "", {
				duration: 3000,
				panelClass: "error-snackbar"
			});
		}
	}

	/**
	 * Check network connectivity before login attempt
	 */
	async checkNetworkConnectivity(): Promise<boolean> {
		try {
			const networkStatus = await Network.getStatus();
			return networkStatus.connected;
		} catch (error) {
			return true; // Assume connected if check fails
		}
	}

	/**
	 * Handle iOS network errors with automatic retry
	 */
	handleIOSNetworkError() {
		const data = this.loginForm.getRawValue();

		// Show initial error message
		this.snackbar.open(
			this.STranslate.instant("login.ios_network_error") ||
				"Erreur de connexion iOS. Tentative de reconnexion automatique...",
			"",
			{
				duration: 3000,
				panelClass: "warning-snackbar"
			}
		);

		// Retry after 2 seconds
		setTimeout(() => {
			if (this.networkStatus) {
				this.attemptLoginWithRetry(data);
			} else {
				this.snackbar.open(
					this.STranslate.instant("login.ios_offline_error") ||
						"Connexion réseau toujours indisponible. Veuillez vérifier votre connexion.",
					"",
					{
						duration: 5000,
						panelClass: "error-snackbar"
					}
				);
			}
		}, 2000);
	}

	/**
	 * Attempt login with retry logic for iOS
	 */
	private attemptLoginWithRetry(data: any, retryCount: number = 0) {
		const maxRetries = 2;

		this.SAuth.loginUser(data.email.toLowerCase(), data.password).subscribe({
			next: async (user) => {
				try {
					await this.SAuth.afterLoginSteps(user);
				} catch (error) {
					this.snackbar.open(this.STranslate.instant("snackbar.cannot_login"), "", {
						duration: 3000,
						panelClass: "error-snackbar"
					});
				}
			},
			error: (error) => {
				if (
					retryCount < maxRetries &&
					this.platform.is("ios") &&
					error.code === "auth/network-request-failed"
				) {
					console.error(`Retry attempt ${retryCount + 1} for iOS login`);
					setTimeout(
						() => {
							this.attemptLoginWithRetry(data, retryCount + 1);
						},
						1000 * (retryCount + 1)
					); // Exponential backoff
				} else {
					this.snackbar.open(
						this.STranslate.instant("login.ios_login_failed") ||
							"Échec de connexion sur iOS après plusieurs tentatives. Veuillez réessayer.",
						"",
						{
							duration: 5000,
							panelClass: "error-snackbar"
						}
					);
					this.store.dispatch(Loading({ payload: false }));
				}
			}
		});
	}

	/**
	 * Login user
	 */
	async login() {
		this.activeLoader.set(true);
		// Validate form state before processing
		if (!this.isLoginFormValid()) {
			this.activeLoader.set(false);
			return;
		}

		// Check network connectivity, especially important for iOS
		const isConnected = await this.checkNetworkConnectivity();
		if (!isConnected && this.platform.is("ios")) {
			this.snackbar.open(
				this.STranslate.instant("login.ios_offline_error") ||
					"Vous êtes hors ligne. Vérifiez votre connexion réseau et réessayez.",
				"",
				{
					duration: 5000,
					panelClass: "error-snackbar"
				}
			);
			return;
		}

		const data = this.loginForm.getRawValue();
		try {
			this.store.dispatch(Loading({ payload: true }));
			if (data.password) {
				if (data.termCheck) {
					this.SEvents.unsubscribeAll();
					this.store.dispatch(InitAuth({ payload: false }));
					this.store.dispatch(InitEvents({ payload: false }));
					this.store.dispatch(InitUser({ payload: false }));
					this.SAuth.loginUser(data.email.toLowerCase(), data.password).subscribe({
						next: async (user) => {
							try {
								await this.SAuth.afterLoginSteps(user);
							} catch (error) {
								this.activeLoader.set(false);
								this.snackbar.open(this.STranslate.instant("snackbar.cannot_login"), "", {
									duration: 3000,
									panelClass: "error-snackbar"
								});
							}
						},
						error: (error) => {
							this.store.dispatch(Loading({ payload: false }));
							this.activeLoader.set(false);
							if (error.code === "auth/wrong-password") {
								this.snackbar.open(this.STranslate.instant("login.incorrect_pass"), "", {
									duration: 3000,
									panelClass: "error-snackbar"
								});
							} else if (error.code == "auth/too-many-requests") {
								this.snackbar.open(this.STranslate.instant("login.many_pass_errors"), "", {
									duration: 3000,
									panelClass: "error-snackbar"
								});
							} else if (error.code == "auth/argument-error") {
								this.snackbar.open(this.STranslate.instant("login.invalid_pass"), "", {
									duration: 3000,
									panelClass: "error-snackbar"
								});
							} else if (error.code === "auth/network-request-failed") {
								// Gestion spécifique pour iOS avec retry automatique
								if (this.platform.is("ios")) {
									this.handleIOSNetworkError();
								} else {
									this.snackbar.open(this.STranslate.instant("snackbar.no_network"), "", {
										duration: 3000,
										panelClass: "error-snackbar"
									});
								}
							} else {
								this.snackbar.open(this.STranslate.instant("snackbar.cannot_login"), "", {
									duration: 3000,
									panelClass: "error-snackbar"
								});
							}
						}
					});
				} else {
					this.store.dispatch(Loading({ payload: false }));
					this.activeLoader.set(false);
					this.snackbar.open(this.STranslate.instant("login.term_check_required"), "", {
						duration: 3000,
						panelClass: "error-snackbar"
					});
				}
			} else {
				this.store.dispatch(Loading({ payload: false }));
				this.activeLoader.set(false);
				this.snackbar.open(this.STranslate.instant("login.invalid_pass"), "", {
					duration: 3000,
					panelClass: "error-snackbar"
				});
			}
		} catch (error) {
			this.store.dispatch(Loading({ payload: false }));
			this.activeLoader.set(false);
			this.snackbar.open(this.STranslate.instant("snackbar.cannot_login"), "", {
				duration: 3000,
				panelClass: "error-snackbar"
			});
		}
	}

	async loginWithOIDC() {
		try {
			this.store.dispatch(Loading({ payload: true }));
			// Init store loader
			this.SEvents.unsubscribeAll();
			this.store.dispatch(InitAuth({ payload: false }));
			this.store.dispatch(InitEvents({ payload: false }));
			this.store.dispatch(InitUser({ payload: false }));
			// First login with google
			const result = await this.SAuth.loginWithOIDC(
				this.container.uid,
				this.container.loginSettings.oidcProvider ? this.container.loginSettings.oidcProvider : "oidc.testidp1"
			);
			if (!Capacitor.isNativePlatform()) {
				// Second check if user data exist
				this.user = await firstValueFrom(this.SAuth.getUserByEmail(result.user.email));
				// Then do the update and redirect
				this.SAuth.afterLoginSteps(result);
			}
		} catch (error: any) {
			this.store.dispatch(Loading({ payload: false }));
			this.snackbar.open(this.STranslate.instant("snackbar.errors.basic"), "", {
				duration: 3000,
				panelClass: "error-snackbar"
			});
		}
	}

	async loginWithGoogle() {
		try {
			this.store.dispatch(Loading({ payload: true }));
			// Init store loader
			this.SEvents.unsubscribeAll();
			this.store.dispatch(InitAuth({ payload: false }));
			this.store.dispatch(InitEvents({ payload: false }));
			this.store.dispatch(InitUser({ payload: false }));
			// First login with google
			const result = await this.SAuth.loginWithGoogle(this.container.uid);

			if (!Capacitor.isNativePlatform()) {
				// Second check if user data exist
				this.user = await firstValueFrom(this.SAuth.getUserByEmail(result.user.email));
				// Then do the update and redirect
				this.SAuth.afterLoginSteps(result);
			} else {
				this.store.dispatch(Loading({ payload: false }));
			}
		} catch (error: any) {
			this.store.dispatch(Loading({ payload: false }));
			if (error.code === "auth/account-exists-with-different-credential") {
				this.snackbar.open(this.STranslate.instant("login.account_exists_with_different_credential"), "", {
					duration: 3000,
					panelClass: "error-snackbar"
				});
			} else {
				this.snackbar.open(this.STranslate.instant("snackbar.errors.basic"), "", {
					duration: 3000,
					panelClass: "error-snackbar"
				});
			}
		}
	}

	async loginWithMicrosoft() {
		try {
			this.store.dispatch(Loading({ payload: true }));
			// Init store loader
			this.SEvents.unsubscribeAll();
			this.store.dispatch(InitAuth({ payload: false }));
			this.store.dispatch(InitEvents({ payload: false }));
			this.store.dispatch(InitUser({ payload: false }));
			// First login with google
			const result = await this.SAuth.loginWithMicrosoft(this.container.uid);
			if (!Capacitor.isNativePlatform()) {
				// Second check if user data exist
				this.user = await firstValueFrom(this.SAuth.getUserByEmail(result.user.email));
				// Then do the update and redirect
				this.SAuth.afterLoginSteps(result);
			}
		} catch (error: any) {
			this.store.dispatch(Loading({ payload: false }));
			if (error.code === "auth/account-exists-with-different-credential") {
				this.snackbar.open(this.STranslate.instant("login.account_exists_with_different_credential"), "", {
					duration: 3000,
					panelClass: "error-snackbar"
				});
			} else {
				this.snackbar.open(this.STranslate.instant("snackbar.errors.basic"), "", {
					duration: 3000,
					panelClass: "error-snackbar"
				});
			}
		}
	}

	/**
	 * Create code number
	 */
	async createCodeNumber() {
		try {
			const result = await firstValueFrom(
				this.SAuth.sendCodeNumber(this.user.uid, this.emailCodeLanguage, this.container.uid)
			);

			if (result.code === 200 && result.user && result.user.codeNumber) {
				// If sending code ok
				this.codeNumber = result.user.codeNumber;

				this.count = 180;
				this.interval = interval(1000).subscribe(() => {
					const date = new Date(null);
					date.setSeconds(this.count);
					this.timeRemaining = date.toISOString().substring(14, 19);
					this.count--;

					if (this.count === 0) {
						if (this.interval && !this.interval.closed) {
							this.interval.unsubscribe();
						}
						this.snackbar.open(this.STranslate.instant("login.resend_code_number"), "", {
							duration: 3000,
							panelClass: "warning-snackbar"
						});
					}
				});
				this.activeLoader.set(false);
			} else {
				this.snackbar.open(this.STranslate.instant("login.error_send_email_code"), "", {
					duration: 3000,
					panelClass: "error-snackbar"
				});
				this.activeLoader.set(false);
			}
		} catch (error) {
			this.snackbar.open(this.STranslate.instant("login.error_send_email_code"), "", {
				duration: 3000,
				panelClass: "error-snackbar"
			});
			this.activeLoader.set(false);
		}
	}

	/**
	 * In case of first access, the user needs to verify your identity
	 * by code number sended to e-mail and create your account
	 */
	async verifyCodeNumber() {
		// Validate if already loading
		if (this.activeLoader()) {
			return;
		}

		const data = this.currentOtpValue; //this.loginForm.getRawValue();
		try {
			if (this.count > 0) {
				if (data) {
					this.activeLoader.set(true);
					const isCodeValid: any = await firstValueFrom(
						this.SAuth.verifyCodeNumber(data.trim(), this.user.uid)
					);

					if (isCodeValid) {
						// If code is ok, go to create password view
						// Change view to first access view or create pass view
						this.changeView(false, true, false, false, false);
						this.activeLoader.set(false);
						//stop interval
						if (this.interval && !this.interval.closed) {
							this.interval.unsubscribe();
						}
					} else {
						this.snackbar.open(this.STranslate.instant("login.invalid_code"), "", {
							duration: 3000,

							panelClass: "error-snackbar"
						});
						this.otpInputComponent?.clear();
						this.activeLoader.set(false);
					}
				} else {
					this.snackbar.open(this.STranslate.instant("login.invalid_code"), "", {
						duration: 3000,
						panelClass: "error-snackbar"
					});
					this.otpInputComponent?.clear();
					this.activeLoader.set(false);
				}
			} else {
				this.activeLoader.set(false);
				this.snackbar.open(this.STranslate.instant("login.error_send_email_code"), "", {
					duration: 3000,
					panelClass: "error-snackbar"
				});
			}
		} catch (error) {
			//console.error("Error: ", error);
			this.activeLoader.set(false);
			this.snackbar.open(this.STranslate.instant("login.error_send_email_code"), "", {
				duration: 3000,
				panelClass: "error-snackbar"
			});
			this.otpInputComponent?.clear();
		}
	}

	/**
	 * Recover password
	 * @param email
	 */
	async recoveryPassword() {
		try {
			const email = this.loginForm.getRawValue().email;
			if (email) {
				this.activeLoader.set(true);
				await firstValueFrom(this.SAuth.recoveryPassword(email.toLowerCase(), this.container.uid, this.event));
				this.presentAlertConfirm();
				this.activeLoader.set(false);
			}
		} catch (error) {
			this.activeLoader.set(false);
			this.snackbar.open(this.STranslate.instant("login.recovery_fail"), "", {
				duration: 3000,
				panelClass: "error-snackbar"
			});
		}
	}

	/**
	 * recoveryPasswordFromSMTPFirebase
	 */
	async recoveryPasswordFromSMTPFirebase() {
		try {
			await this.SAuth.recoveryPasswordFromFirebase(this.loginForm.getRawValue().email);

			this.snackbar.open(this.STranslate.instant("login.recovery_success"), "", {
				duration: 3000,
				panelClass: "success-snackbar"
			});
		} catch (error) {
			this.snackbar.open(this.STranslate.instant("login.recovery_fail"), "", {
				duration: 3000,
				panelClass: "error-snackbar"
			});
		}
	}

	async presentAlertConfirm() {
		const alert = await this.alertCtrl.create({
			header: this.STranslate.instant("login.recovery_success"),
			message: this.STranslate.instant("login.recovery_text"),
			buttons: [
				{
					text: "Ok",
					handler: () => {
						alert.dismiss();
					}
				},
				{
					text: this.STranslate.instant("login.resend_email_reset_link"),
					handler: () => {
						this.recoveryPasswordFromSMTPFirebase();
					}
				}
			],
			cssClass: "my-custom-alert",
			backdropDismiss: false
		});

		await alert.present();
	}

	async presentAlertConfirmWithMessage(title: string, message: string) {
		const alert = await this.alertCtrl.create({
			header: title,
			message: message,
			buttons: [
				{
					text: "Ok",
					handler: () => {
						alert.dismiss();
					}
				}
			],
			cssClass: "my-custom-alert",
			backdropDismiss: false
		});

		await alert.present();
	}

	/**
	 * Changing view
	 * @param emailView
	 * @param createPasswordView
	 * @param passwordView
	 * @param firstAccessView
	 * @param shortcodeView
	 */
	changeView(
		emailView: boolean,
		createPasswordView: boolean,
		passwordView: boolean,
		firstAccessView: boolean,
		shortcodeView: boolean
	) {
		if (createPasswordView) {
			if (this.container.loginSettings.passwordSecurityLevel) {
				if (this.container.loginSettings.passwordSecurityLevel === PasswordSecurity.BASIC) {
					if (this.loginForm.get("password").hasValidator(this.SForms.strongPasswordValidator())) {
						this.loginForm.get("password").removeValidators(this.SForms.strongPasswordValidator());
					}
					this.loginForm.get("password").addValidators(this.SForms.basicPasswordValidator());
				} else {
					if (this.loginForm.get("password").hasValidator(this.SForms.basicPasswordValidator())) {
						this.loginForm.get("password").removeValidators(this.SForms.basicPasswordValidator());
					}
					this.loginForm.get("password").addValidators(this.SForms.strongPasswordValidator());
				}

				this.loginForm.get("password").updateValueAndValidity();
				this.loginForm.get("confirmPassword").updateValueAndValidity();
			}
		}
		this.emailView = emailView;
		this.createPasswordView = createPasswordView;
		this.passwordView = passwordView;
		this.firstAccessView = firstAccessView;
		this.shortcodeView = shortcodeView;
	}

	/**
	 * Open terms or privacy modal
	 * @param type
	 * @returns
	 */
	async openModalTermsOrPrivacy(type: string) {
		const modal = await this.modalCtrl.create({
			component: type === "privacy" ? PathComponents.privacy : PathComponents.termsOfUse,
			componentProps:
				type === "privacy"
					? {
							privacy: this.container.privacyTerms
						}
					: {
							terms: this.container.termsOfUse
						}
		});
		return await modal.present();
	}

	/**
	 * Open short code alert
	 */
	async openShortcodeCtrl() {
		const data = this.loginForm.getRawValue();
		const prompt = await this.alertCtrl.create({
			header: this.STranslate.instant("buttons.access_by_shortcode"),
			message: this.STranslate.instant("alerts.type_event_shortcode"),
			inputs: [
				{
					name: "shortcode",
					placeholder: this.STranslate.instant("labels.code"),
					value: data.shortcode ? data.shortcode : "",
					type: "text"
				}
			],
			buttons: [
				{
					text: this.STranslate.instant("buttons.cancel"),
					role: "cancel"
				},
				{
					text: this.STranslate.instant("buttons.btn_verify"),
					handler: (dataAlert) => {
						this.checkShortCode(dataAlert, "alert");
					}
				}
			]
		});
		prompt.present();
	}

	/**
	 * Checking shortcode
	 * @param dataAlert
	 * @param type The source of the call ('alert' or 'shortcodeView')
	 */
	async checkShortCode(dataAlert: any, type: string) {
		const shortcode =
			type === "shortcodeView" ? this.loginForm.getRawValue().shortcode : dataAlert.shortcode.toLowerCase();
		// Check if shortcode input is filled
		if (shortcode) {
			this.loginForm.patchValue({
				shortcode: shortcode
			});
			try {
				const event = await firstValueFrom(this.SEvents.getEventByShortcode(shortcode.toLowerCase()));
				if (!event) {
					// Case event doesn't exist
					this.snackbar.open(this.STranslate.instant("snackbar.event_shortcode_blank"), "", {
						duration: 3000,
						panelClass: "error-snackbar"
					});
					this.loginForm.patchValue({
						shortcode: ""
					});
				} else {
					if (!event.settings.visibility) {
						// Case event is private
						this.presentAlertConfirmWithMessage(
							this.STranslate.instant("login.attempt_to_login_with_short_code_private_event_title"),
							this.STranslate.instant("login.attempt_to_login_with_short_code_private_event_msg")
						);
						return;
					}
					this.store.dispatch(Loading({ payload: true }));
					this.navCtrl.navigateRoot([`/shortcode/${shortcode}`]);
					this.loginForm.patchValue({
						shortcode: ""
					});
					this.elementRef.nativeElement.remove();
				}
			} catch (error) {
				this.snackbar.open(this.STranslate.instant("snackbar.error_occured"), "", {
					duration: 3000,
					panelClass: "error-snackbar"
				});
			}
		} else {
			// case blank, show error
			this.snackbar.open(this.STranslate.instant("snackbar.event_shortcode_blank"), "", {
				duration: 3000,
				panelClass: "error-snackbar"
			});
		}
	}

	/**
	 * Back to app login
	 */
	backToAppLogin() {
		this.event = null;
		this.store.dispatch(Loading({ payload: true }));
		this.navCtrl.navigateRoot(["/"]);
		this.elementRef.nativeElement.remove();
	}

	/**
	 * Go to public events list
	 */
	goPublicEventsList() {
		this.store.dispatch(Loading({ payload: true }));
		this.navCtrl.navigateRoot("/events-list/publics");
		this.elementRef.nativeElement.remove();
	}

	/**
	 * getDeviceLanguage
	 * @description Get email code language
	 */
	async getDeviceLanguage() {
		try {
			const langTag = await Device.getLanguageTag();
			if (["fr", "en", "pt", "de", "es"].includes(langTag.value.split("-")[0])) {
				const lang = langTag.value.split("-")[0];
				switch (lang) {
					case "ar":
						this.emailCodeLanguage = "ArAR";
						break;
					case "fr":
						this.emailCodeLanguage = "FrFR";
						break;
					case "en":
						this.emailCodeLanguage = "EnUS";
						break;
					case "pt":
						this.emailCodeLanguage = "PtBR";
						break;
					case "de":
						this.emailCodeLanguage = "DeDE";
						break;
					case "es":
						this.emailCodeLanguage = "EsES";
						break;
				}
			}
		} catch (error) {
			//console.error("LoginPage ~ getEmailCodeLanguage ~ error", error);
		}
	}

	/**
	 * Handles the OTP change event from the otp-input component.
	 * @param otp The current OTP value.
	 */
	onOtpChange(otp: string): void {
		this.currentOtpValue = otp;
		if (otp.length < 8) {
			this.isShortcodeOtpComplete = false;
		}
	}

	/**
	 * Handles the OTP complete event from the otp-input component.
	 * @param otp The completed OTP value.
	 */
	onOtpComplete(otp: string): void {
		this.currentOtpValue = otp;
		this.isShortcodeOtpComplete = otp.length === 8;

		this.verifyCodeNumber();
	}

	/**
	 * Validates if login form is ready for submission
	 * @returns boolean indicating if form is valid
	 */
	private isLoginFormValid(): boolean {
		const data = this.loginForm.getRawValue();

		// Check if already loading
		// if (this.activeLoader) {
		// 	return false;
		// }

		// Check email validity
		if (!data.email) {
			this.store.dispatch(Loading({ payload: false }));
			this.snackbar.open(this.STranslate.instant("login.invalid_email"), "", {
				duration: 3000,
				panelClass: "error-snackbar"
			});
			return false;
		}

		// Check password
		if (!data.password || data.password.trim().length === 0) {
			this.store.dispatch(Loading({ payload: false }));
			this.snackbar.open(this.STranslate.instant("login.invalid_pass"), "", {
				duration: 3000,
				panelClass: "error-snackbar"
			});
			return false;
		}

		// Check terms acceptance
		if (!data.termCheck) {
			this.store.dispatch(Loading({ payload: false }));
			this.snackbar.open(this.STranslate.instant("login.term_check_required"), "", {
				duration: 3000,
				panelClass: "error-snackbar"
			});
			return false;
		}

		return true;
	}
}
